import { ErrorBanner, RileyRail } from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import * as React from 'react'
import { describeError } from '../../lib/api/client'
import { CaseSummaryContent, MissingCaseSummary } from '../case-summary'
import { RETIRED_CARD_KINDS, RileyChat } from './chat'
import { chats, resumeChatTurn, setChatThread, useSummaryCollapsed } from './chat-store'
import type { RileyChatThread, RileyRailFeatureProps } from './contracts'
import { RileyRatingsProvider } from './feedback'
import { InvestigateButton, InvestigationOutcome, useInvestigation } from './investigate'
import { scrollViewportTo } from './scroll'

/** The store key of a case's Riley conversation: the same after a remount, a layout switch or navigating back. */
export const caseChatKey = (caseId: string) => `case:${caseId}`

export function RileyCaseRail({
  caseId,
  deskTicketId,
  caseSummary,
  onBuildSummary,
  onFocusTarget,
  dataSource,
  context,
  initialThread,
  initialTrace = [],
  viewerId,
  viewerRole,
  mode,
  onModeChange,
}: RileyRailFeatureProps) {
  const key = caseChatKey(caseId)
  // The thread lives in the chat store (patchbay#196): a remount shows what is there at once and refreshes quietly.
  const entry = chats.use(key)
  const thread: RileyChatThread | null = entry.thread ?? initialThread ?? null
  const [loading, setLoading] = React.useState(!thread)
  const [error, setError] = React.useState<string | null>(null)
  // Bumped by the "Reload thread" option: re-runs startChat (resume) for the same case.
  const [reloads, setReloads] = React.useState(0)
  const investigation = useInvestigation({ caseId, dataSource })
  const tidyServiceHistory = dataSource.tidyServiceHistory
  const [summaryCollapsed, setSummaryCollapsed] = useSummaryCollapsed(viewerId ?? 'anonymous', caseId)
  // #681: card.proposed / riley.draft_suggested name ['riley-rail', case uuid]. Refresh the stored thread when that
  // key is invalidated. A turn the person is waiting on is left in place; the chat box keeps its own text.
  const railThreadId = thread?.id
  const railSeededAt = React.useRef(Date.now())
  const refresh = useQuery({
    queryKey: ['riley-rail', caseId],
    enabled: Boolean(railThreadId) && Boolean(dataSource.getChat),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: null as RileyChatThread | null,
    initialDataUpdatedAt: railSeededAt.current,
    queryFn: async () => {
      if (chats.get(key).pending) return chats.get(key).thread
      const id = chats.get(key).thread?.id ?? railThreadId
      const getChat = dataSource.getChat
      if (!id || !getChat) return chats.get(key).thread
      const next = await getChat(id)
      if (!chats.get(key).pending) setChatThread(key, next)
      return next
    },
  })

  React.useEffect(() => {
    if (initialThread && reloads === 0) {
      if (!chats.get(key).pending) setChatThread(key, initialThread)
      setLoading(false)
      return
    }
    let active = true
    if (!chats.get(key).thread) setLoading(true)
    setError(null)
    void dataSource
      .startChat({ case_id: caseId, desk_ticket_id: deskTicketId, scope: 'case', resume: true })
      .then((next) => {
        if (!active) return
        // Mid-turn, the stored copy holds the question being answered; the turn settles the thread itself.
        if (!chats.get(key).pending) setChatThread(key, next)
        const getChat = dataSource.getChat?.bind(dataSource)
        if (getChat) void resumeChatTurn(key, () => getChat(next.id))
      })
      .catch((reason: unknown) => {
        if (active && !chats.get(key).thread) setError(describeError(reason, 'Riley chat is unavailable'))
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [caseId, dataSource, deskTicketId, initialThread, key, reloads])

  const investigationBusy = investigation.status === 'queued' || investigation.status === 'running'
  const feedback = dataSource.submitFeedback?.bind(dataSource)
  const getChat = dataSource.getChat?.bind(dataSource)
  const undismiss = dataSource.undismissCard?.bind(dataSource)
  const moveToReplyBox = dataSource.moveToReplyBox?.bind(dataSource)
  const undoBulk = dataSource.undoBulkCard?.bind(dataSource)
  const undoTriage = dataSource.undoTriageCard?.bind(dataSource)
  const undoStaff = dataSource.undoStaffAction?.bind(dataSource)
  const cardProgress = dataSource.cardProgress?.bind(dataSource)
  const trace = [...initialTrace, ...investigation.trace]
  const rootRef = React.useRef<HTMLDivElement>(null)
  // Cards waiting for a decision; the header badge jumps to the first one (#417).
  const pendingCount = (thread?.cards ?? []).filter((card) => card.state === 'proposed' && !RETIRED_CARD_KINDS.has(card.kind)).length
  const jumpToPending = () => {
    const viewport = rootRef.current?.querySelector<HTMLElement>('[data-slot="riley-chat-scroll"]')
    const first = viewport?.querySelector('[data-card-pending="true"]')
    if (viewport && first) scrollViewportTo(viewport, first)
  }
  const ratedIds = [
    ...(thread?.messages ?? []).filter((message) => message.role === 'riley').map((message) => message.id),
    ...(thread?.cards ?? []).map((card) => card.id),
    ...(investigation.id ? [investigation.id] : []),
  ]
  const chat = thread ? (
    <>
      {refresh.isError ? (
        // The thread shown is the last copy; a failed refresh must say so rather than pass for current (rule 10).
        <ErrorBanner
          tone="warning"
          className="mb-2"
          title="Riley's thread could not refresh"
          detail={`${describeError(refresh.error)}. Showing the last copy.`}
          onRetry={() => void refresh.refetch()}
        />
      ) : null}
      <RileyChat
        thread={thread}
        chatKey={key}
        viewerId={viewerId}
        viewerRole={viewerRole}
        onSend={(message, clientMessageId) => dataSource.postMessage(thread.id, { message, client_message_id: clientMessageId })}
        onRecover={getChat ? () => getChat(thread.id) : undefined}
        onConfirmCard={(card, payload, acknowledged) => dataSource.confirmCard(card.id, payload, acknowledged)}
        onDismissCard={(card, reason) => dataSource.dismissCard(card.id, reason ?? undefined)}
        onTidyServiceHistory={tidyServiceHistory ? (card, text) => tidyServiceHistory(card.id, text) : undefined}
        onUndismissCard={undismiss ? (card) => undismiss(card.id) : undefined}
        onUseInReplyBox={moveToReplyBox ? (card) => moveToReplyBox(card.id) : undefined}
        onBulkUndoCard={undoBulk ? (card) => undoBulk(card.id) : undefined}
        onTriageUndoCard={undoTriage ? (card) => undoTriage(card.id) : undefined}
        onStaffUndoCard={
          undoStaff
            ? async (card) => {
                const actionId = String((card.result as Record<string, unknown> | null | undefined)?.action_id ?? '')
                await undoStaff(actionId)
                return { ...card, result: { ...(card.result ?? {}), can_undo: false, undone: true } }
              }
            : undefined
        }
        onFeedback={feedback}
        onCardProgress={cardProgress}
        onJump={onFocusTarget}
        /* chat is always chronological, input at the bottom, docked or expanded (Clayton, 2026-10-18) */
        order="oldest"
      />
      <div className="mt-3">
        <InvestigationOutcome state={investigation} viewerRole={viewerRole} onFeedback={feedback} />
      </div>
    </>
  ) : loading ? (
    <p className="text-sm text-muted-foreground">Opening the case thread…</p>
  ) : (
    <ErrorBanner title="Riley chat is unavailable" detail={error ?? undefined} onRetry={() => setReloads((n) => n + 1)} />
  )

  return (
    <div ref={rootRef} className="contents">
      <RileyRatingsProvider ids={ratedIds} load={dataSource.getFeedback?.bind(dataSource)}>
        <RileyRail
          pending={pendingCount ? { count: pendingCount, onJump: jumpToPending } : undefined}
          mode={mode}
          onModeChange={onModeChange}
          summary={caseSummary ? <CaseSummaryContent summary={caseSummary} /> : <MissingCaseSummary onBuild={onBuildSummary} />}
          summaryCollapsed={summaryCollapsed}
          onSummaryCollapsedChange={setSummaryCollapsed}
          context={context}
          trace={trace}
          traceActions={<InvestigateButton state={investigation} viewerRole={viewerRole} />}
          chat={chat}
          options={[
            ...(viewerRole !== 'billing'
              ? [{ label: 'Investigate', onSelect: () => void investigation.start(), disabled: investigationBusy }]
              : []),
            { label: 'Reload thread', onSelect: () => setReloads((n) => n + 1), disabled: loading },
          ]}
        />
      </RileyRatingsProvider>
    </div>
  )
}
